import React, { useEffect, useState } from "react";
import { ClockIcon, PhoneIcon, VideoCameraIcon, VideoCameraSlashIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { WebRTCProvider, useWebRTC } from "../../contexts/WebRTCContext";
import { useSocket } from "../../contexts/SocketContext";
import { Panel } from "../../components/panels/Panel";
import { CamerasGrid } from "./components/CamerasGrid";
import { ActionButton } from "../../components/buttons/ActionButton";
import { useAlert } from "../../contexts/AlertContext";
import { calendarDateTimetoDate, formatDateTimeFromTimestamp, formatSecondsToFullDateTime, formatSecondsToMinutesSeconds } from "../../helpers/date/date-helper";
import { DateRangePicker } from "@heroui/date-picker";
import { Tooltip } from "@heroui/react";
import Badge from "../../components/badges/Badge";
import { isMobileDevice } from "../../helpers/common/isMobileDevice";

const PhoneOffIcon = ({ className='' }) => (
  <span className={`relative inline-flex ${className}`}>
    <PhoneIcon className="h-full w-full"/>
    <span className="absolute left-1/2 top-1/2 h-0.5 w-[125%] -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded-full bg-current"/>
  </span>
);

const SessionButton = ({ join=false, onClick, disabled=false }) => {
  return <ActionButton text={join ? 'Rejoindre' : 'Quitter la session'} Icon={join ? PhoneIcon : PhoneOffIcon} color={join ? 'success' : 'danger'} onClick={onClick} disabled={disabled}/>;
};

export const RecorderMeetPage = () => {
  return (
    <WebRTCProvider meetMode={true}>
      <RecorderMeetLayout/>
    </WebRTCProvider>
  );
};

const RecorderMeetLayout = () => {
  const { cams, isParticipant, isJoiningParticipant, joinParticipant, leaveParticipant, isRecording, recordingStartedAt, startMeetRecording, stopMeetRecording, scheduledJob, setScheduledJob, clearScheduledJobLocal, currentJob } = useWebRTC();
  const { socket } = useSocket();
  const { showAlert } = useAlert();

  const [showJoinPanel, setShowJoinPanel] = useState(true);
  const [recordingSeconds, setRecordingSeconds] = useState(null);

  const now = new Date();
  const nextHour = new Date(now); nextHour.setHours(nextHour.getHours() + 1);
  const defaultRange = { start: now, end: nextHour };

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [draftRange, setDraftRange] = useState({ start: formatDateTimeFromTimestamp(defaultRange.start), end: formatDateTimeFromTimestamp(defaultRange.end) });

  const isMobile = isMobileDevice();
  const hasScheduledJob = scheduledJob !== null;

  const isDraftInPast = (() => {
    if (!draftRange?.start) return true;
    const startTs = calendarDateTimetoDate(draftRange.start).getTime();
    return startTs <= Date.now();
  })();

  useEffect(() => {
    if (!isRecording || recordingStartedAt === null) {
      setRecordingSeconds(null);
      return;
    }

    const update = () => setRecordingSeconds(Math.max(0, Math.floor(Date.now() / 1000) - recordingStartedAt));
    update();

    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [isRecording, recordingStartedAt]);

  useEffect(() => {
    if (!socket) return;

    const handleRecordingError = ({ code }) => {
      if (code === 'no_recordable_participant') {
        clearScheduledJobLocal();
        showAlert('error', 'Aucun participant disponible pour l\'enregistrement.');
        return;
      }

      if (code === 'recording_start_failed') {
        clearScheduledJobLocal();
        showAlert('error', 'Impossible de démarrer l\'enregistrement.');
      }
    };

    socket.on('media_recording_error', handleRecordingError);
    return () => socket.off('media_recording_error', handleRecordingError);
  }, [socket, showAlert, clearScheduledJobLocal]);

  const handleJoin = async () => {
    try {
      await joinParticipant();
      setShowJoinPanel(false);
    } catch (error) {
      if (error.code === 'publisher_already_exists') showAlert('error', 'Ce device participe déjà à la session depuis un autre onglet.');
      else showAlert('error', 'Impossible de rejoindre la session. Vérifiez l’accès à la caméra ou au microphone.');
    }
  };

  const handleLeave = async () => {
    await leaveParticipant();
    setShowJoinPanel(true);
  };

  const handleStartRecording = async () => {
    try {
      await startMeetRecording();
    } catch (error) {
      if (error.code === 'no_recordable_participant') showAlert('error', 'Aucun participant disponible pour l\'enregistrement.');
      else showAlert('error', 'Impossible de démarrer l\'enregistrement.');
    }
  };

  const handleStopRecording = async () => {
    try {
      await stopMeetRecording();

      if (currentJob !== null) {
        clearScheduledJobLocal();
        fetch(`${process.env.REACT_APP_ADDRESS}streaming/clear_schedule`, { method: "POST", headers: { "Content-Type": "application/json" } }).catch(() => {});
      }
    } catch {
      showAlert('error', 'Impossible d\'arrêter l\'enregistrement.');
    }
  };

  const handleScheduleRecording = async () => {
    const start = Math.floor(calendarDateTimetoDate(draftRange.start).getTime() / 1000);
    const end = Math.floor(calendarDateTimetoDate(draftRange.end).getTime() / 1000);

    const res = await fetch(`${process.env.REACT_APP_ADDRESS}streaming/schedule/${start}/${end}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mode: 'stream', meet: true }),
    });

    if (!res.ok) {
      showAlert('error', 'Impossible de programmer l\'enregistrement.');
      return;
    }

    setScheduledJob({ start, end });
    showAlert('success', 'L\'enregistrement a été programmé. Veillez à ne pas arrêter le système.');
  };

  const handleCancelScheduledRecording = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_ADDRESS}streaming/clear_schedule`, { method: "POST", headers: { "Content-Type": "application/json" } });

      if (!res.ok) {
        showAlert('error', 'Impossible d\'annuler l\'enregistrement programmé.');
        return;
      }

      clearScheduledJobLocal();
      showAlert('success', 'L\'enregistrement programmé a été annulé.');
    } catch {
      showAlert('error', 'Impossible d\'annuler l\'enregistrement programmé.');
    }
  };

  return (
    <div className="h-full flex">
      <div className="h-full flex-1 min-w-0">
        <Panel category="main" className="flex flex-col items-center relative" noPadding noHeader>
          <div className="h-full flex flex-col">
            <div className="flex-1 min-h-0">
              {
                cams.length ?
                  <CamerasGrid meetMode={true}/>
                  :
                  <div className="h-full flex items-center justify-center px-4">
                    <p className="text-center text-xl md:text-3xl text-perception-gray-600">Aucun participant pour le moment</p>
                  </div>
              }
            </div>

            <div className="shrink-0 px-1 pt-1 pb-2 md:px-4 md:pt-0 md:pb-4 overflow-x-auto">
              <div className="w-max min-w-full flex flex-nowrap justify-center gap-2 md:gap-4 items-center whitespace-nowrap">
                {
                  isRecording &&
                  <div className="shrink-0 flex items-center gap-2 text-sm md:text-base">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-error"/>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-error"/>
                    </span>
                    {
                      isMobile ?
                        recordingSeconds !== null ? formatSecondsToMinutesSeconds(recordingSeconds) : null
                        :
                        <>Enregistrement en cours{recordingSeconds !== null ? ` (${formatSecondsToMinutesSeconds(recordingSeconds)})` : ''}</>
                    }
                  </div>
                }

                {
                  isParticipant && !isMobile && (
                    isRecording ?
                      <ActionButton text="Arrêter l'enregistrement" color="secondary" Icon={VideoCameraSlashIcon} onClick={handleStopRecording}/>
                      :
                      <>
                        <Tooltip content="Un enregistrement est déjà programmé." showArrow={true} isDisabled={!hasScheduledJob}>
                          <ActionButton text="Démarrer l'enregistrement" Icon={VideoCameraIcon} onClick={handleStartRecording} disabled={!socket || hasScheduledJob}/>
                        </Tooltip>

                        <div className="relative">
                          <Tooltip content="Un enregistrement est déjà programmé." showArrow={true} isDisabled={!hasScheduledJob}>
                            <ActionButton text="Programmer" Icon={ClockIcon} onClick={() => setIsCalendarOpen(!isCalendarOpen)} disabled={hasScheduledJob}/>
                          </Tooltip>

                          <DateRangePicker
                            isOpen={isCalendarOpen}
                            onOpenChange={setIsCalendarOpen}
                            value={{ start: draftRange.start, end: draftRange.end }}
                            onChange={({ start, end }) => setDraftRange({ start, end })}
                            CalendarBottomContent={
                              <div className="flex justify-end gap-2 p-4 pt-0">
                                <ActionButton text="Annuler" color="cancel" onClick={() => setIsCalendarOpen(false)} size="sm"/>
                                <ActionButton text="Programmer" size="sm" disabled={!draftRange?.start || !draftRange?.end || isDraftInPast} onClick={() => { handleScheduleRecording(); setIsCalendarOpen(false); }}/>
                              </div>
                            }
                            classNames={{
                              base: "p-0",
                              inputWrapper: "p-0 min-h-0 h-auto bg-transparent",
                              selectorButton: "hidden",
                              innerWrapper: "absolute w-0 h-0 overflow-hidden opacity-0 pointer-events-none",
                              segment: "hidden",
                              separator: "hidden",
                              input: "w-0 h-0 p-0 m-0 opacity-0",
                              label: "hidden",
                            }}
                            className="absolute left-0 top-0 w-0 h-0 overflow-hidden p-0 m-0"
                            radius="none"
                            granularity="minute"
                            label={null}
                          />
                        </div>
                      </>
                  )
                }

                {
                  scheduledJob && !isRecording && (
                    isMobile ?
                      <div className="shrink-0 flex items-center gap-1.5 text-xs">
                        <ClockIcon className="size-4"/>
                        <span>Programmé</span>
                      </div>
                      :
                      <div className="shrink-0 flex items-center gap-2 text-sm">
                        Enregistrement programmé
                        <Badge text={formatSecondsToFullDateTime(scheduledJob.start)} type="green"/>
                        {
                          isParticipant &&
                          <Tooltip content="Annuler l'enregistrement" showArrow={true}>
                            <ActionButton color="secondary" size="sm" Icon={XMarkIcon} onClick={handleCancelScheduledRecording}/>
                          </Tooltip>
                        }
                      </div>
                  )
                }

                {
                  isParticipant ?
                    <SessionButton onClick={handleLeave}/>
                    : (isMobile || !showJoinPanel) ?
                      <SessionButton join={true} onClick={handleJoin} disabled={isJoiningParticipant}/>
                      : null
                }
              </div>
            </div>
          </div>
        </Panel>
      </div>

      {
        !isMobile && showJoinPanel && !isParticipant &&
        <div className="h-full w-1/5 min-w-64 p-4 bg-perception-black-900">
          <div
            role="button"
            tabIndex={0}
            aria-disabled={isJoiningParticipant}
            className={`group relative h-full w-full rounded-md bg-perception-black-500 flex flex-col items-center justify-center gap-5 text-center cursor-pointer transition-all hover:scale-[1.01] hover:bg-perception-blue/40 ${isJoiningParticipant ? 'opacity-50 pointer-events-none' : ''}`}
            onClick={handleJoin}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              handleJoin();
            }}
          >
            <button
              type="button"
              aria-label="Fermer"
              className="absolute top-4 right-4 p-2 rounded-md bg-white/10 backdrop-blur-lg text-white transition hover:bg-white/20"
              onClick={(event) => {
                event.stopPropagation();
                setShowJoinPanel(false);
              }}
            >
              <XMarkIcon className="size-5"/>
            </button>

            <div className="p-5 rounded-full bg-white/10 backdrop-blur-lg transition-transform group-hover:scale-105">
              <PhoneIcon className="size-20 text-white"/>
            </div>

            <div>
              <p className="mt-4 text-xl text-perception-gray-500 transition-colors group-hover:text-white">Rejoindre la session</p>
              <p className="mt-2 text-sm text-perception-gray-600 transition-colors group-hover:text-white">Cliquer pour participer</p>
            </div>
          </div>
        </div>
      }
    </div>
  );
};